import { useCommunityInboxStore } from "@circle-react/hooks/chatsV2/store";
import { useChatStore } from "@circle-react/hooks/chatsV2/store/useChatStore";
import { useWebSocket } from "@circle-react/hooks/useWebSocket";
import type { Chat } from "@circle-react/types/CommunityInbox/Chat";
import { useRef } from "react";

import { selectInboxMessages } from "@/react/hooks/chatsV2/store/selectors";

import { MESSAGE_CONTAINER_ID } from "./scrollConstants";
import { useAvatarReconnect } from "./useAvatarReconnect";

interface AvatarReplySnapshot {
  message_id: number;
  seq: number;
  text: string | null;
}

const FOLLOW_THRESHOLD_PX = 120;

const followIfNearBottom = () => {
  const container = document.getElementById(MESSAGE_CONTAINER_ID);
  if (!container) return;

  const distanceFromBottom =
    container.scrollHeight - container.scrollTop - container.clientHeight;
  if (distanceFromBottom > FOLLOW_THRESHOLD_PX) return;

  // Wait for the Markdown preview to render the new body before measuring.
  requestAnimationFrame(() =>
    requestAnimationFrame(() => {
      container.scrollTop = container.scrollHeight;
    }),
  );
};

// Writes streamed snapshots into the placeholder message; the saved message replaces it once delivered.
export const useAvatarReplyStream = (
  chat: Chat | null | undefined,
  isDms: boolean,
) => {
  useAvatarReconnect(chat, isDms);
  const { updateMessage } = useCommunityInboxStore();
  const lastSeqRef = useRef<Record<number, number>>({});

  useWebSocket({
    channel: "Avatars::ChatStreamChannel",
    chat_id: chat?.id,
    canCreateConnection: !!chat?.streaming_enabled && !!chat.is_processing,
    onMessageReceive: ({ message_id, seq, text }: AvatarReplySnapshot) => {
      if (seq <= (lastSeqRef.current[message_id] ?? 0)) return;

      const message = selectInboxMessages(useChatStore.getState()).find(
        ({ id }) => id === message_id,
      );
      if (!message?.is_processing) return;

      lastSeqRef.current[message_id] = seq;
      followIfNearBottom();
      updateMessage({ ...message, streaming_text: text });
    },
  });
};
